import { Group, Stack, Text } from '@mantine/core';
import {
  GOREV_HEDEF_BILGI_METNI,
  GOREV_HEDEF_BILGI_SEBEPLERI,
  GOREV_HEDEF_ENGEL_METNI,
  GOREV_HEDEF_ENGEL_SEBEPLERI,
} from '@isg/validation';
import type { GorevHedefDurumu } from '@isg/validation';
import type { RozetAnlami } from '../theme';
import { Rozet } from '../sozlesme';

/**
 * PERİYODİK GÖREV — ORTAK PARÇALAR (F4·S5·B2).
 *
 * ⚠️ **DURUM → ROZET ANLAMI EŞLEMESİ TEK YERDE.** Liste, detay ve ızgara
 * aynı satırı farklı renkte gösterirse kullanıcı hangisine bakacağını bilemez.
 * Bileşen ANLAM alır (P3-b), renk `theme.ts`ten çözülür — burada renk adı
 * geçmez.
 *
 * Dört durumun anlamı (Z5 — üçü ayrı şey söyler):
 *   yapildi   → **olumlu**   (dönem yerine getirildi)
 *   kacirildi → **gecikmiş** (kapandı ama YERİNE GETİRİLMEDİ — denetim izi)
 *   gecersiz  → **bilgi**    (pasif ekipman: kimse yapmadı ama gerekmiyordu
 *                             da; `kacirildi` göstermek OLMAYAN bir
 *                             uygunsuzluk üretirdi)
 *   bekliyor  → vadesi geçtiyse **dikkat**, geçmediyse **bilgi**
 */
export const GOREV_DURUM_ETIKET: Record<string, string> = {
  bekliyor: 'Bekliyor',
  yapildi: 'Yapıldı',
  kacirildi: 'Kaçırıldı',
  gecersiz: 'Geçersiz',
};

export function gorevRozetAnlami(satir: { durum: string; vadesiGecti: boolean }): {
  anlam: RozetAnlami;
  etiket: string;
} {
  if (satir.durum === 'yapildi') return { anlam: 'olumlu', etiket: 'Yapıldı' };
  if (satir.durum === 'kacirildi') return { anlam: 'gecikmis', etiket: 'Kaçırıldı' };
  if (satir.durum === 'gecersiz') return { anlam: 'bilgi', etiket: 'Geçersiz' };
  return satir.vadesiGecti
    ? { anlam: 'dikkat', etiket: 'Vadesi geçti' }
    : { anlam: 'bilgi', etiket: 'Bekliyor' };
}

export function GorevRozeti({
  satir,
  testId = 'gorev-rozeti',
}: {
  satir: { durum: string; vadesiGecti: boolean };
  testId?: string;
}): React.JSX.Element {
  const { anlam, etiket } = gorevRozetAnlami(satir);
  return (
    <Rozet anlam={anlam} testId={testId}>
      {etiket}
    </Rozet>
  );
}

/**
 * GÖREV ATAMA UYARISI — hedef durumu (sessiz 403 paketi, 29.09.2026; Kapsam
 * §22.6a). Atama satırı · tanım listesi · görev detayı AYNI bileşeni çizer;
 * veri sunucunun TEK yükleminden gelir (`gorevHedefDurumu`) — ekran hesap,
 * şapka ya da bahçe TÜRETMEZ.
 *
 * ⚠️ **İKİ AYRI DİL.** *Engel* uyarıdır (rozet) ve EYLEM ister: kişinin hesabı
 * yok ya da kapalı. *Bilgi* uyarı DEĞİLDİR (soluk metin, rozet/ikon yok) ve
 * eylem istemez: kişi henüz ilk girişini yapmadı (GA-4 — ilk girişinde görevi
 * görecek) ya da görevi atama yetkisiyle yapacak. İkisini aynı rozetle çizmek,
 * kendiliğinden çözülen bir hâli arıza gibi gösterirdi (uyarı yorgunluğu).
 * Sınıfı SUNUCU ayırır; ekran bir kişinin hangi sınıfa düştüğüne karar vermez.
 *
 * ⚠️ **UYARI ÇÖZÜM YOLUNU SÖYLER** (madde 288): her engel SEBEP taşır ve cümle
 * sebebe göre `GOREV_HEDEF_ENGEL_METNI`nden gelir — ekran metin yazmaz. Küme
 * hedefinde aynı sebepli üyeler TEK satırda adlarıyla listelenir.
 *
 * ⚠️ **ATAMAYI ENGELLEMEZ** — sistem uyarır, atama yapılır.
 */
export function HedefDurumuSatirlari({
  durum,
  kume,
}: {
  durum: GorevHedefDurumu;
  /** Küme hedefinde adlar listelenir; kişi hedefinde ad zaten satırdadır. */
  kume: boolean;
}): React.JSX.Element | null {
  if (durum.engeller.length === 0 && durum.bilgiler.length === 0) return null;
  const adlar = (kisiler: readonly { adSoyad: string }[]): string =>
    kisiler.map((k) => k.adSoyad).join(', ');
  // Sebep başına TEK satır — sıra sözlüğün sırasıdır (ekran sıralama yazmaz).
  const engelGruplari = GOREV_HEDEF_ENGEL_SEBEPLERI.map((sebep) => ({
    sebep,
    kisiler: durum.engeller.filter((e) => e.sebep === sebep),
  })).filter((grup) => grup.kisiler.length > 0);
  const bilgiGruplari = GOREV_HEDEF_BILGI_SEBEPLERI.map((sebep) => ({
    sebep,
    kisiler: durum.bilgiler.filter((b) => b.sebep === sebep),
  })).filter((grup) => grup.kisiler.length > 0);
  return (
    <Stack gap={2} mt={4}>
      {engelGruplari.map(({ sebep, kisiler }) => (
        <Group
          key={sebep}
          gap={6}
          wrap="nowrap"
          align="flex-start"
          data-testid="gorev-hedef-engeli"
          data-sebep={sebep}
        >
          <Rozet anlam="dikkat">
            {kume
              ? GOREV_HEDEF_ENGEL_METNI[sebep].kumeRozeti
              : GOREV_HEDEF_ENGEL_METNI[sebep].rozet}
          </Rozet>
          <Text size="xs">
            {kume ? `${adlar(kisiler)} — ` : ''}
            {GOREV_HEDEF_ENGEL_METNI[sebep].cozum}
          </Text>
        </Group>
      ))}
      {bilgiGruplari.map(({ sebep, kisiler }) => (
        <Text key={sebep} size="xs" c="dimmed" data-testid="gorev-hedef-bilgisi" data-sebep={sebep}>
          {kume
            ? `${GOREV_HEDEF_BILGI_METNI[sebep].kume}: ${adlar(kisiler)}.`
            : GOREV_HEDEF_BILGI_METNI[sebep].kisi}
        </Text>
      ))}
    </Stack>
  );
}
